/**
 * web/src/components/gates/CopyRationaleCard.tsx
 *
 * Renders Claude's one-sentence rationale for why a particular copy
 * variant was chosen for an email-gate body. Surfaces above EmailPreview
 * on LLM-driven gates (ops_next_steps, welcome_email).
 *
 * Two visual states:
 *   - Happy path (`source === 'llm'`): a quiet, compact, collapsed-by-default
 *     <details> disclosure. The summary is a single muted line with the
 *     variant + confidence badges; the reasoning text only appears on click.
 *     Goal: don't compete with the email content for attention.
 *   - Fallback (`source === 'fallback'`): a louder red disclosure expanded
 *     by default. Reviewer needs to see immediately that the body is the
 *     raw Mustache template, not Claude's selection.
 *
 * Hidden when the gate doesn't expose copyReasoning so non-LLM gates
 * render the page identically to before.
 *
 * Wire shape (GateDataEmailKind.context):
 *   {
 *     copyReasoning: string,
 *     copyVariantLabel?: string,
 *     copyConfidence?: 'high' | 'medium' | 'low',
 *     copySource?: 'llm' | 'fallback',
 *     copyFallbackReason?: string | null,
 *   }
 */
import { Badge } from '../shared/Badge';
import { Icon } from '../../lib/icons';
import { cn } from '../../lib/cn';

/**
 * Narrow Record<string, unknown> shape pulled from gateData.context.
 * All fields are optional so the card degrades gracefully if buildGate
 * shipped only a subset (e.g., a fallback path with no variant label).
 */
export interface CopyRationale {
  copyReasoning?: unknown;
  copyVariantLabel?: unknown;
  copyConfidence?: unknown;
  copySource?: unknown;
  copyFallbackReason?: unknown;
}

/**
 * Reads optional copy-rationale fields from a gate's `context` blob and
 * returns a typed shape suitable for rendering. Returns null when no
 * rationale is present so the caller can no-op without rendering the
 * card frame (welcome_email + data-gates).
 *
 * @param context the GateDataEmailKind.context (raw Record<string, unknown>)
 * @returns typed rationale fields, or null when no copyReasoning was emitted
 */
export function readCopyRationale(context: Record<string, unknown> | undefined): {
  reasoning: string;
  variantLabel: string | null;
  confidence: 'high' | 'medium' | 'low' | null;
  source: 'llm' | 'fallback';
  fallbackReason: string | null;
} | null {
  if (!context) return null;
  const reasoning = context.copyReasoning;
  if (typeof reasoning !== 'string' || reasoning.length === 0) return null;
  const variantLabel =
    typeof context.copyVariantLabel === 'string' ? context.copyVariantLabel : null;
  const confidenceRaw = context.copyConfidence;
  const confidence =
    confidenceRaw === 'high' || confidenceRaw === 'medium' || confidenceRaw === 'low'
      ? confidenceRaw
      : null;
  const source = context.copySource === 'fallback' ? 'fallback' : 'llm';
  const fallbackReason =
    typeof context.copyFallbackReason === 'string' ? context.copyFallbackReason : null;
  return { reasoning, variantLabel, confidence, source, fallbackReason };
}

/**
 * Quiet disclosure shown above EmailPreview on LLM-driven email gates.
 * Collapsed by default on the happy path so it doesn't steal attention
 * from the email content; expanded by default on fallback because the
 * reviewer needs to know the body isn't Claude's selection.
 *
 * @sideEffects none — pure presentation
 * @idempotent yes
 */
export function CopyRationaleCard({
  rationale,
  className,
}: {
  rationale: NonNullable<ReturnType<typeof readCopyRationale>>;
  className?: string;
}) {
  const isFallback = rationale.source === 'fallback';
  return (
    <details
      open={isFallback}
      className={cn(
        'group rounded-md border text-[12px]',
        isFallback ? 'border-failed-border bg-failed-soft' : 'border-border bg-bg-soft',
        className,
      )}
    >
      <summary
        className={cn(
          'flex items-center gap-2 px-3 py-2 cursor-pointer select-none list-none',
          'marker:hidden [&::-webkit-details-marker]:hidden',
          isFallback ? 'text-failed' : 'text-text-muted',
        )}
      >
        <Icon
          name="chevron-right"
          size={12}
          className="shrink-0 transition-transform group-open:rotate-90"
        />
        <Icon name={isFallback ? 'alert' : 'info'} size={12} className="shrink-0" />
        <span className={cn('truncate', isFallback ? 'font-semibold' : 'font-medium')}>
          {isFallback
            ? 'Claude copy fill FAILED — raw template below'
            : 'Claude selected this copy'}
        </span>
        <div className="flex-1" />
        {rationale.variantLabel && (
          <Badge variant="default" title="Chosen variant">
            {rationale.variantLabel}
          </Badge>
        )}
        {rationale.confidence && (
          <Badge
            variant={
              rationale.confidence === 'high'
                ? 'completed'
                : rationale.confidence === 'medium'
                  ? 'waiting'
                  : 'failed'
            }
            title="Self-reported confidence"
          >
            {rationale.confidence}
          </Badge>
        )}
      </summary>
      <div
        className={cn('px-3 pb-2.5 pt-0 leading-[1.55]', isFallback ? 'text-failed' : 'text-text')}
      >
        {rationale.reasoning}
        {isFallback && rationale.fallbackReason && (
          <div className="mt-1.5 text-[11px] text-text-muted">
            Reviewer: the body below is the raw template with deterministic placeholder
            substitutions only — double-check before sending.
          </div>
        )}
      </div>
    </details>
  );
}
